import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, ArrowRight, GraduationCap, CheckCircle2, XCircle, Lock, Check, Linkedin, Facebook, Link as LinkIcon } from "lucide-react";
import { MODULES } from "../lib/trainingContent";
import { useSeo } from "../lib/useSeo";
import api from "../lib/api";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";

function CongratsModal({ name, onView, onClose }) {
  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60 backdrop-blur-sm px-4" data-testid="training-congrats-modal">
      <div className="bg-white rounded-3xl max-w-md w-full p-8 text-center shadow-2xl border-4 border-[#D4AF37]">
        <p className="text-5xl">🎉</p>
        <h2 className="font-display text-2xl font-extrabold mt-3">Congratulations{name ? `, ${name.split(" ")[0]}` : ""}!</h2>
        <p className="text-slate-600 text-sm mt-3">You have completed the <strong>XpressPrompts™ Starter Training</strong> and are now certified on the AI Short Command Prompts Training Course.</p>
        <p className="text-emerald-700 text-sm font-bold mt-3">📧 Your personalised certificate is on its way to your email as a printable PDF.</p>
        <button onClick={onView} data-testid="congrats-view-certificate-btn"
          className="mt-6 w-full bg-emerald-700 hover:bg-emerald-800 text-white font-bold py-3 rounded-xl transition-colors">
          View my certificate →
        </button>
        <button onClick={onClose} data-testid="congrats-close-btn" className="mt-2 w-full text-slate-400 hover:text-slate-600 text-xs font-bold py-2">Close</button>
      </div>
    </div>
  );
}

function Certificate({ name, date, certId }) {
  const [copied, setCopied] = useState(false);
  const shareUrl = certId ? `${process.env.REACT_APP_BACKEND_URL}/api/certificates/${certId}` : null;
  return (
    <div className="mt-12 max-w-3xl mx-auto" data-testid="training-certificate">
      <div className="rounded-2xl border-4 border-[#D4AF37] bg-[#FDFBF5] p-8 sm:p-12 text-center shadow-lg">
        <div className="border border-emerald-700/40 rounded-xl px-4 py-8 sm:py-10">
          <img src="/assets/logo.png" alt="XpressPrompts™" className="w-16 h-16 mx-auto" draggable={false} />
          <p className="font-display text-[#B8962E] font-bold text-[11px] tracking-[0.3em] uppercase mt-4">XpressPrompts™</p>
          <h2 className="font-display text-2xl sm:text-3xl font-extrabold tracking-tight mt-3">Certificate of Completion</h2>
          <p className="text-sm text-slate-500 mt-5">This is to certify that</p>
          <p className="font-display text-2xl sm:text-3xl font-bold italic text-emerald-800 mt-2" data-testid="certificate-name">{name}</p>
          <div className="w-48 h-px bg-[#D4AF37] mx-auto mt-2" />
          <p className="text-sm text-slate-500 mt-4">has been certified as having completed the</p>
          <p className="font-display font-extrabold text-lg sm:text-xl mt-1.5">AI Short Command Prompts Training Course</p>
          <p className="text-[#B8962E] font-bold text-xs tracking-[0.2em] uppercase mt-3">— Certified Completed —</p>
          <p className="text-sm text-slate-600 mt-4" data-testid="certificate-date">Date of completion: <strong>{date}</strong></p>
          <p className="text-[10px] text-slate-400 mt-6">XpressPrompts™ · Universal Short-Command Prompt Dictionary™ · Voliciti Group Ventures Ltd</p>
        </div>
      </div>
      <p className="text-center text-xs text-slate-500 mt-3">📧 A printable PDF copy of your certificate has been emailed to you.</p>
      {shareUrl && (
        <div className="flex flex-wrap items-center justify-center gap-2.5 mt-5" data-testid="certificate-share">
          <span className="text-xs font-bold text-slate-600 w-full text-center sm:w-auto">Share my achievement:</span>
          <button data-testid="cert-share-linkedin"
            onClick={() => window.open(`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareUrl)}`, "_blank", "noopener,width=600,height=560")}
            className="inline-flex items-center gap-1.5 bg-[#0A66C2] text-white text-xs font-bold px-4 py-2 rounded-lg hover:opacity-90 transition-opacity">
            <Linkedin size={13} /> LinkedIn
          </button>
          <button data-testid="cert-share-facebook"
            onClick={() => window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`, "_blank", "noopener,width=600,height=560")}
            className="inline-flex items-center gap-1.5 bg-[#1877F2] text-white text-xs font-bold px-4 py-2 rounded-lg hover:opacity-90 transition-opacity">
            <Facebook size={13} /> Facebook
          </button>
          <button data-testid="cert-share-copy"
            onClick={() => { navigator.clipboard.writeText(shareUrl); setCopied(true); setTimeout(() => setCopied(false), 2000); }}
            className="inline-flex items-center gap-1.5 bg-slate-900 text-white text-xs font-bold px-4 py-2 rounded-lg hover:bg-slate-700 transition-colors">
            <LinkIcon size={13} /> {copied ? "Copied!" : "Copy link"}
          </button>
        </div>
      )}
    </div>
  );
}

const DISCLAIMER = "© Voliciti Group Ventures Ltd · XpressPrompts™. This training material is provided for members' personal use only and must not be copied, reproduced or shared outside the platform.";

function QuizQuestion({ idx, q }) {
  const [picked, setPicked] = useState(null);
  return (
    <div className="bg-slate-50 rounded-xl p-4 mt-3" data-testid={`quiz-q-${idx}`}>
      <p className="font-bold text-sm">{idx + 1}. {q.q}</p>
      <div className="space-y-2 mt-3">
        {q.opts.map((o, i) => {
          const state = picked === null ? "" : i === q.a ? "correct" : i === picked ? "wrong" : "";
          return (
            <button key={i} onClick={() => setPicked(i)} data-testid={`quiz-q${idx}-opt${i}`}
              className={`w-full text-left text-sm rounded-lg px-3.5 py-2.5 border flex items-center gap-2 transition-colors ${
                state === "correct" ? "bg-emerald-50 border-emerald-400 text-emerald-800 font-semibold"
                : state === "wrong" ? "bg-red-50 border-red-300 text-red-700"
                : "bg-white border-slate-200 hover:border-slate-400"}`}>
              {state === "correct" && <CheckCircle2 size={15} className="shrink-0" />}
              {state === "wrong" && <XCircle size={15} className="shrink-0" />}
              {o}
            </button>
          );
        })}
      </div>
      {picked !== null && (
        <p className={`text-xs font-bold mt-2 ${picked === q.a ? "text-emerald-700" : "text-red-600"}`}>
          {picked === q.a ? "Correct!" : "Not quite — the highlighted answer is correct."}
        </p>
      )}
    </div>
  );
}

function Block({ b }) {
  if (b.t === "h") return <h3 className="font-display font-extrabold text-lg mt-8">{b.v}</h3>;
  if (b.t === "p") return <p className="text-[15px] text-slate-700 leading-relaxed mt-4">{b.v}</p>;
  if (b.t === "ul") return <ul className="mt-4 space-y-1.5">{b.v.map((li, i) => <li key={i} className="text-[15px] text-slate-700 flex gap-2"><span className="text-emerald-600 font-bold">•</span>{li}</li>)}</ul>;
  if (b.t === "cmd") return <div className="mt-4 bg-[#0B0F0D] text-emerald-300 font-mono text-sm rounded-xl px-4 py-3.5 leading-relaxed">{b.v}</div>;
  if (b.t === "note") return <div className="mt-4 bg-amber-50 border-l-4 border-[#D4AF37] rounded-r-xl px-4 py-3.5 text-sm text-slate-800 leading-relaxed font-medium">{b.v}</div>;
  if (b.t === "quiz") return <div>{b.v.map((q, i) => <QuizQuestion key={i} idx={i} q={q} />)}</div>;
  if (b.t === "video") return (
    <div className="mt-5 rounded-2xl overflow-hidden border border-slate-200 shadow-sm bg-black" data-testid="training-video">
      <video src={b.v} controls controlsList="nodownload noremoteplayback" disablePictureInPicture playsInline preload="metadata"
        onContextMenu={(e) => e.preventDefault()} className="w-full aspect-video" />
    </div>
  );
  if (b.t === "audio") return (
    <div className="mt-5 rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3" data-testid="training-audio">
      <p className="text-[11px] font-bold uppercase tracking-[0.14em] text-[#B8962E] mb-2">🎧 {b.label || "Listen to this module"}</p>
      <audio src={b.v} controls controlsList="nodownload" onContextMenu={(e) => e.preventDefault()} className="w-full" />
    </div>
  );
  return null;
}

function ModuleCard({ m, onOpen, isDone }) {
  return (
    <button onClick={onOpen} data-testid={`training-card-${m.id}`}
      className={`text-left bg-white border rounded-2xl overflow-hidden hover:shadow-md transition-all group ${isDone ? "border-emerald-400" : "border-slate-200 hover:border-emerald-500/50"}`}>
      <div className="relative">
        <img src="/assets/training-banner.webp" alt={`XpressPrompts™ ${m.badge}`} className="w-full" draggable={false} />
        <span className="absolute top-2.5 left-2.5 bg-gradient-to-r from-amber-500 to-yellow-500 text-slate-950 text-[11px] font-extrabold tracking-wide px-3 py-1 rounded-full shadow">{m.badge}</span>
        {isDone && (
          <span className="absolute top-2.5 right-2.5 bg-emerald-600 text-white rounded-full p-1.5 shadow" data-testid={`training-tick-${m.id}`}>
            <Check size={13} strokeWidth={3.5} />
          </span>
        )}
      </div>
      <div className="p-4">
        <h3 className="font-display font-bold text-sm">{m.title}</h3>
        <p className="text-xs text-slate-500 mt-1">{m.subtitle}</p>
        <span className={`mt-3 text-xs font-bold flex items-center gap-1 ${isDone ? "text-emerald-600" : "text-emerald-700"}`}>
          {isDone ? <><CheckCircle2 size={13} /> Completed — open again</> : <>Open module <ArrowRight size={12} /></>}
        </span>
      </div>
    </button>
  );
}

export default function TrainingPage() {
  const [open, setOpen] = useState(null);
  const [done, setDone] = useState([]);
  const [completedAt, setCompletedAt] = useState(null);
  const [certId, setCertId] = useState(null);
  const [showCongrats, setShowCongrats] = useState(false);
  const navigate = useNavigate();
  const { user } = useAuth();

  useEffect(() => {
    api.get("/training/progress").then((r) => { setDone(r.data.completed || []); setCompletedAt(r.data.completed_at || null); setCertId(r.data.cert_id || null); }).catch(() => {});
  }, []);

  const toggleDone = (id) => {
    const marking = !done.includes(id);
    const hadCert = !!completedAt;
    setDone((d) => (marking ? [...d, id] : d.filter((x) => x !== id)));
    api.post("/training/progress", { module_id: id, done: marking }).then((r) => {
      setDone(r.data.completed || []);
      setCompletedAt(r.data.completed_at || null);
      setCertId(r.data.cert_id || null);
      if (marking && r.data.completed_at && !hadCert) setShowCongrats(true);
      else if (marking) toast.success("Module marked complete ✓");
    }).catch(() => {});
  };

  const viewCertificate = () => {
    setShowCongrats(false);
    setOpen(null);
    setTimeout(() => document.querySelector('[data-testid="training-certificate"]')?.scrollIntoView({ behavior: "smooth", block: "center" }), 300);
  };
  useSeo({ title: "XpressPrompts™ Starter Training", description: "Members-only starter training — learn to talk to AI in shorthand.", noindex: true });

  const guard = { style: { userSelect: "none", WebkitUserSelect: "none" }, onContextMenu: (e) => e.preventDefault(), onCopy: (e) => e.preventDefault() };
  const mod = MODULES.find((m) => m.id === open);
  const modIdx = mod ? MODULES.indexOf(mod) : -1;

  if (mod) {
    return (
      <div className="bg-[#FAFAF7] min-h-screen py-10 px-4 sm:px-6 lg:px-8" data-testid="training-module-view" {...guard}>
        <div className="max-w-3xl mx-auto">
          {showCongrats && <CongratsModal name={user?.name} onView={viewCertificate} onClose={() => setShowCongrats(false)} />}
          <button onClick={() => { setOpen(null); window.scrollTo(0, 0); }} data-testid="training-back-btn"
            className="inline-flex items-center gap-1.5 text-sm font-bold text-emerald-700 hover:text-emerald-800">
            <ArrowLeft size={15} /> All training modules
          </button>
          <div className="relative mt-4 rounded-2xl overflow-hidden">
            <img src="/assets/training-banner.webp" alt={`XpressPrompts™ ${mod.badge}`} className="w-full" draggable={false} />
            <span className="absolute top-3 left-3 bg-gradient-to-r from-amber-500 to-yellow-500 text-slate-950 text-xs font-extrabold tracking-wide px-3.5 py-1.5 rounded-full shadow">{mod.badge}</span>
          </div>
          <div className="bg-white border border-slate-200 rounded-2xl p-6 sm:p-10 mt-4">
            <h1 className="font-display text-2xl sm:text-3xl font-extrabold tracking-tight">{mod.title}</h1>
            <p className="text-slate-500 text-sm mt-1.5">{mod.subtitle}</p>
            {mod.blocks.map((b, i) => <Block key={i} b={b} />)}
            <div className="mt-10 pt-6 border-t border-slate-100 flex justify-center">
              <button onClick={() => toggleDone(mod.id)} data-testid="training-mark-complete-btn"
                className={`inline-flex items-center gap-2 text-sm font-bold px-6 py-2.5 rounded-xl border transition-colors ${
                  done.includes(mod.id)
                    ? "bg-emerald-600 border-emerald-600 text-white hover:bg-emerald-700"
                    : "bg-white border-slate-300 text-slate-700 hover:border-emerald-500 hover:text-emerald-700"}`}>
                {done.includes(mod.id) ? <><CheckCircle2 size={16} /> Completed — tap to unmark</> : <><Check size={16} /> Mark module as complete</>}
              </button>
            </div>
            <div className="flex items-center justify-between gap-3 mt-6">
              {modIdx > 0 ? (
                <button onClick={() => { setOpen(MODULES[modIdx - 1].id); window.scrollTo(0, 0); }} data-testid="training-prev-btn"
                  className="inline-flex items-center gap-1.5 text-sm font-bold text-slate-600 hover:text-emerald-700"><ArrowLeft size={14} /> Previous</button>
              ) : <span />}
              {modIdx < MODULES.length - 1 ? (
                <button onClick={() => { setOpen(MODULES[modIdx + 1].id); window.scrollTo(0, 0); }} data-testid="training-next-btn"
                  className="inline-flex items-center gap-1.5 bg-slate-900 text-white text-sm font-bold px-5 py-2.5 rounded-xl hover:bg-emerald-700 transition-colors">Next module <ArrowRight size={14} /></button>
              ) : <span />}
            </div>
          </div>
          <p className="flex items-start gap-2 text-[11px] text-slate-400 mt-5 max-w-2xl mx-auto text-center justify-center" data-testid="training-disclaimer">
            <Lock size={12} className="shrink-0 mt-0.5" /> {DISCLAIMER}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-[#FAFAF7] min-h-screen py-12 px-4 sm:px-6 lg:px-8" data-testid="training-page" {...guard}>
      {showCongrats && <CongratsModal name={user?.name} onView={viewCertificate} onClose={() => setShowCongrats(false)} />}
      <div className="max-w-5xl mx-auto">
        <div className="text-center">
          <p className="font-display text-[#D4AF37] font-bold text-xs tracking-[0.16em] uppercase flex items-center justify-center gap-2"><GraduationCap size={16} /> Members-only training</p>
          <h1 className="font-display text-3xl sm:text-4xl font-extrabold tracking-tight mt-3">XpressPrompts™ Starter Training</h1>
          <img src="/assets/training-cert-banner.webp" alt="Complete all modules to earn your XpressPrompts™ Certificate of Completion"
            draggable={false} data-testid="training-cert-banner"
            className="w-full max-w-3xl mx-auto rounded-2xl shadow-md mt-6" />
          <p className="text-slate-500 text-sm sm:text-base mt-3 max-w-xl mx-auto">Talk to AI in Shorthand — work through the modules in order, or jump to the topic you need. Read on-screen at your own pace.</p>
          <div className="mt-5 max-w-xs mx-auto" data-testid="training-progress">
            <p className="text-xs font-bold text-slate-600">{done.length} of {MODULES.length} modules completed</p>
            <div className="mt-2 h-2 rounded-full bg-slate-200 overflow-hidden">
              <div className="h-full rounded-full bg-gradient-to-r from-emerald-500 to-emerald-600 transition-all duration-500" style={{ width: `${(done.length / MODULES.length) * 100}%` }} />
            </div>
          </div>
          {completedAt && (
            <button onClick={viewCertificate} data-testid="training-completed-chip"
              className="mt-4 inline-flex items-center gap-2 bg-emerald-50 border border-emerald-300 text-emerald-800 text-xs font-bold px-4 py-2 rounded-full hover:bg-emerald-100 transition-colors">
              🎉 Training completed on {completedAt} — view your certificate ↓
            </button>
          )}
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 mt-10" data-testid="training-grid">
          {MODULES.map((m) => <ModuleCard key={m.id} m={m} isDone={done.includes(m.id)} onOpen={() => { setOpen(m.id); window.scrollTo(0, 0); }} />)}
        </div>
        {completedAt && <Certificate name={user?.name || user?.email || "Member"} date={completedAt} certId={certId} />}
        <div className="mt-12 max-w-3xl mx-auto" data-testid="training-walkthrough">
          <p className="font-display text-[#B8962E] font-bold text-xs tracking-[0.16em] uppercase text-center">▶ Watch the full walkthrough</p>
          <p className="text-slate-500 text-sm text-center mt-2">The whole step-by-step: how to search, select a command, add your details and get to work.</p>
          <div className="mt-4 rounded-2xl overflow-hidden border border-slate-200 shadow-sm bg-black aspect-video">
            <iframe src="https://www.youtube.com/embed/90wHg4fOaXc" title="XpressPrompts — full step-by-step walkthrough"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen
              className="w-full h-full" data-testid="training-walkthrough-video" />
          </div>
        </div>
        <div className="text-center mt-10">
          <button onClick={() => navigate("/app")} data-testid="training-to-dictionary" className="text-sm font-bold text-emerald-700 hover:text-emerald-800">Ready to practise? Open the Dictionary →</button>
        </div>
        <p className="flex items-start gap-2 text-[11px] text-slate-400 mt-8 max-w-2xl mx-auto justify-center" data-testid="training-hub-disclaimer">
          <Lock size={12} className="shrink-0 mt-0.5" /> {DISCLAIMER}
        </p>
      </div>
    </div>
  );
}
